import { Component, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, clientView as cvStore } from "./api";
import { LangContext, translate, type Lang } from "./i18n";
import { AskDrawer } from "./screens/Ask";
import { Academy } from "./screens/Academy";
import { Announcements, Help } from "./screens/Announcements";
import { Badges } from "./screens/Badges";
import { Bookings } from "./screens/Bookings";
import { Builder } from "./screens/Builder";
import { Clients } from "./screens/Clients";
import { Earnings } from "./screens/Earnings";
import { Events, Forum } from "./screens/Community";
import { Home } from "./screens/Home";
import { Inbox } from "./screens/Inbox";
import { Lounge } from "./screens/Lounge";
import { Packages } from "./screens/Packages";
import { Performance } from "./screens/Performance";
import { Perks } from "./screens/Perks";
import { Refer } from "./screens/Refer";
import { SignIn } from "./screens/SignIn";
import { Trips } from "./screens/Trips";
import { I, initials, ShellContext, type Me, type Shell } from "./ui";

/** The screens of the prototype's navigation (docs/37 §1); agency users do not see badges, perks or the Lounge. */
const VIEWS = ["home", "inbox", "announcements", "performance", "earnings", "badges", "packages", "bookings", "trips", "builder", "clients", "perks", "refer", "forum", "events", "academy", "help"] as const;
type View = (typeof VIEWS)[number];
const EXPERT_ONLY: View[] = ["badges", "perks"];

const viewOf = (hash: string): View => {
  const v = hash.replace(/^#/, "").split("/")[0] as View;
  return VIEWS.includes(v) ? v : "home";
};

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "signed-out" | "ready">("loading");
  const [view, setView] = useState<View>(viewOf(location.hash));
  const [clientView, setClientView] = useState(cvStore.get());
  const [toastMsg, setToast] = useState<string>();
  const [lounge, setLounge] = useState<{ onDone?: (() => void) | undefined } | null>(null);
  const [ask, setAsk] = useState<{ text?: string | undefined; quoteId?: string | undefined } | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [q, setQ] = useState("");
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const reload = useCallback(async () => {
    try {
      setMe(await api<Me>("/me"));
      setState("ready");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 409)) setState("signed-out");
      else throw e;
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    const on = () => { setView(viewOf(location.hash)); setSideOpen(false); window.scrollTo(0, 0); };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  useEffect(() => { document.body.classList.toggle("client-view", clientView); }, [clientView]);

  const lang: Lang = me?.partner.language ?? (navigator.language.startsWith("fr") ? "fr" : "en");
  const t = (k: string, v?: Record<string, string | number>) => translate(lang, k, v);
  const shell: Shell | null = useMemo(() => (me ? {
    me, reload,
    go: (v: string) => { if (location.hash === `#${v}`) setView(viewOf(v)); else location.hash = v; },
    toast: (msg: string) => { setToast(msg); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToast(undefined), 2600); },
    openLounge: (onDone?: () => void) => setLounge({ onDone }),
    ask: (text?: string, quoteId?: string) => setAsk({ text, quoteId }),
    clientView,
  } : null), [me, reload, clientView]);

  if (state === "loading") return <div className="boot"><div className="mark">G</div></div>;
  if (state === "signed-out" || !me || !shell) return <LangContext.Provider value={lang}><SignIn /></LangContext.Provider>;

  const expert = me.expert;
  const shown = expert || !EXPERT_ONLY.includes(view) ? view : "home";
  const hour = new Date().getHours();
  const first = me.session.name.split(" ")[0] ?? me.session.name;
  const title = shown === "home" ? t(hour < 12 ? "greet.morning" : hour < 18 ? "greet.afternoon" : "greet.evening", { name: first }) : t(`nav.${shown}`);
  const nav = (v: View, icon: ReactNode, extra?: ReactNode) => (
    <button className="nav" type="button" aria-current={shown === v ? "page" : "false"} onClick={() => shell.go(v)}>{icon}{t(`nav.${v}`)}{extra}</button>
  );
  const toggleCv = () => { const next = !clientView; cvStore.set(next); setClientView(next); };
  const search = async () => {
    if (q.trim().length < 2) return;
    location.hash = "packages";
    sessionStorage.setItem("station.pkq", q.trim());
    window.dispatchEvent(new Event("station:pkq"));
  };

  return (
    <LangContext.Provider value={lang}>
      <ShellContext.Provider value={shell}>
        <div className="app">
          <nav className="rail" aria-label="Apps">
            <div className="mark" title="The Getaways Experts">G</div>
            <button type="button" aria-label={t("nav.home")} aria-current={shown === "home"} onClick={() => shell.go("home")}>{I.home}</button>
            <button type="button" aria-label={t("nav.packages")} aria-current={shown === "packages"} onClick={() => shell.go("packages")}>{I.pk}</button>
            {expert ? <button type="button" aria-label={t("nav.perks")} aria-current={shown === "perks"} onClick={() => shell.go("perks")}>{I.gift}</button> : null}
            <button type="button" aria-label={t("nav.builder")} aria-current={shown === "builder"} onClick={() => shell.go("builder")}>{I.compass}</button>
            {expert ? <button type="button" aria-label={t("nav.lounge")} onClick={() => shell.openLounge()}>{I.plane}</button> : null}
            <div className="grow" />
            <div className="avatar" title={me.session.name}>{initials(me.session.name)}</div>
          </nav>

          <aside className={`side${sideOpen ? " open" : ""}`} id="side" aria-label="Navigation">
            <div className="brand"><b>The Station</b><span>{expert ? t("brand.sub") : me.partner.displayName}</span></div>
            <button className="cta-build" type="button" onClick={() => shell.go("builder")}>{I.plus}{t("nav.build")}</button>
            <div className="group">
              <span>{t("nav.group.home")}</span>
              {nav("home", I.dash)}{nav("inbox", I.inbox)}{nav("announcements", I.ann)}{nav("performance", I.perf)}{nav("earnings", I.earn)}{expert ? nav("badges", I.badge) : null}
            </div>
            <div className="group">
              <span>{t("nav.group.sell")}</span>
              {nav("packages", I.pk)}{nav("bookings", I.cal)}{nav("trips", I.trips)}{nav("builder", I.plane, <span className="new">{t("nav.new")}</span>)}{nav("clients", I.people)}
            </div>
            <div className="group">
              <span>{t("nav.group.rewards")}</span>
              {expert ? nav("perks", I.gift) : null}
              {expert ? <button className="nav" type="button" onClick={() => shell.openLounge()}>{I.plane}{t("nav.lounge")}<span className="new">{t("nav.play")}</span></button> : null}
              {nav("refer", I.refer, expert ? <span className="new">$50</span> : null)}
            </div>
            <div className="group">
              <span>{t("nav.group.community")}</span>
              {nav("forum", I.forum)}{nav("events", I.events)}{nav("academy", I.academy)}
            </div>
            <button className="help" type="button" onClick={() => shell.go("help")}>{I.help}{t("nav.help")}<span>›</span></button>
            <button className="help" type="button" onClick={async () => { await api("/auth/logout", { body: {} }); location.reload(); }}>{t("top.signOut")}<span>›</span></button>
          </aside>

          <main>
            <div className="topbar">
              <div className="cv-banner">{t("top.cvBanner")}</div>
              <div className="in">
                <button className="menu-btn" type="button" aria-label="Open menu" onClick={() => setSideOpen((o) => !o)}>{I.menu}</button>
                <h1 id="page-title">{title}</h1>
                <label className="search top-search">{I.search}<input placeholder={t("top.search")} aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void search()} /></label>
                <button className="cv" type="button" role="switch" aria-checked={clientView} title="Hide margins while you share your screen with a client" onClick={toggleCv}><span>{t("top.clientView")}</span><i /></button>
                <button className="concierge" type="button" aria-label={t("top.ask")} onClick={() => shell.ask()}>{I.spark}<span>{t("top.ask")}</span></button>
              </div>
            </div>
            <div className="content" key={shown}><ScreenBoundary>
              {shown === "home" ? <Home /> : shown === "inbox" ? <Inbox /> : shown === "announcements" ? <Announcements /> : shown === "performance" ? <Performance /> : shown === "earnings" ? <Earnings />
                : shown === "badges" ? <Badges /> : shown === "packages" ? <Packages /> : shown === "bookings" ? <Bookings /> : shown === "trips" ? <Trips /> : shown === "builder" ? <Builder />
                : shown === "clients" ? <Clients /> : shown === "perks" ? <Perks /> : shown === "refer" ? <Refer /> : shown === "forum" ? <Forum /> : shown === "events" ? <Events /> : shown === "academy" ? <Academy /> : <Help />}
            </ScreenBoundary></div>
          </main>
        </div>
        {lounge ? <Lounge onClose={() => setLounge(null)} onDone={lounge.onDone} /> : null}
        {ask ? <AskDrawer initial={ask.text} quoteId={ask.quoteId} onClose={() => setAsk(null)} /> : null}
        {toastMsg ? <div className="toast">{toastMsg}</div> : null}
      </ShellContext.Provider>
    </LangContext.Provider>
  );
}

/** A screen that fails shows a message in its place; the shell (and the Client view switch) stays usable. */
class ScreenBoundary extends Component<{ children: ReactNode }, { failed?: string | undefined }> {
  override state: { failed?: string | undefined } = {};
  static getDerivedStateFromError(err: Error) { return { failed: err.message }; }
  override render() {
    if (this.state.failed) return <div className="card empty"><b>This screen could not be shown.</b><p className="muted">{this.state.failed}</p><button className="btn line" type="button" onClick={() => location.reload()}>Reload</button></div>;
    return this.props.children;
  }
}
